"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import {FCWithChildren} from "../../types/reactTypes";
import {setCookie, deleteCookie, getCookie} from "cookies-next";
import {CAMPUS_COOKIE} from "../../../constants/cookies";
import Campus from "@/app/types/Campus";
import fetchCampusMemoized from "@/app/cms/fetchCampusClient";
import {NEXT_SITE_URL_OBJECT} from "@/app/util/urlUtils";

/**
 * Context and helpers for getting and setting the campus cookie via a React context.
 *
 * The context provides the current value and a setter function.
 */
interface CampusCookieContext {
	campus: Campus | null;
	isLoading: boolean;
	isInitialized: boolean;
	setCampus: (campus: string | null) => void;
	deleteCampus: () => void;
}

const CampusCookieContext = createContext<CampusCookieContext>({
	campus: null,
	isLoading: true,
	isInitialized: false,
	setCampus: () => {},
	deleteCampus: () => {},
});

export default CampusCookieContext;

export const useCampusContext = () => {
	return useContext(CampusCookieContext);
};

// helper hook for extracting the cookie campusId specifically from the context.
export const useCookieCampusId = () => {
	const {campus} = useCampusContext();
	return campus?.campusId ?? "";
};

export const CampusCookieProvider = CampusCookieContext.Provider;

export const CampusCookieWrapperClient: FCWithChildren = ({children}) => {
	const [campus, setCampus] = useState<Campus | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);

	const setCampusAndCookie = useCallback(async (newCampus: string | null) => {
		if (!newCampus) {
			return;
		}

		setIsLoading(true);
		const campusInfo = await fetchCampusMemoized(newCampus);
		if (!campusInfo) {
			console.warn(
				`Failed to fetch campus info for ${newCampus}.  Cookie will not be set.`
			);
			setIsLoading(false);
			setIsInitialized(true);
			return;
		}

		setCampus(campusInfo);
		setCookie(CAMPUS_COOKIE, newCampus, {
			domain: `.${NEXT_SITE_URL_OBJECT.hostname}`,
		});
		setIsLoading(false);
		setIsInitialized(true);
	}, []);
	const deleteCampusAndCookie = useCallback(() => {
		setCampus(null);
		deleteCookie(CAMPUS_COOKIE);
		setIsInitialized(true);
	}, []);

	useEffect(() => {
		(async () => {
			const initialCampus = getCookie(CAMPUS_COOKIE);
			if (!initialCampus) {
				setIsLoading(false);
				setIsInitialized(true);
				return;
			}

			setIsLoading(true);
			const initialCampusInfo = await fetchCampusMemoized(initialCampus);
			if (!initialCampusInfo) {
				console.warn(
					`Failed to populate initial campus ${initialCampus}.  Deleting cookie`
				);
				deleteCookie(CAMPUS_COOKIE);
				setIsLoading(false);
				setIsInitialized(true);
				return;
			}

			setCampus(initialCampusInfo);
			setIsLoading(false);
			setIsInitialized(true);
		})();
	}, []);

	return (
		<CampusCookieProvider
			value={{
				campus,
				isLoading,
				isInitialized,
				setCampus: setCampusAndCookie,
				deleteCampus: deleteCampusAndCookie,
			}}
		>
			{children}
		</CampusCookieProvider>
	);
};
